import React from 'react';

/**
 * Stack of small "EPC / BAR / QR / NFC" pills indicating which identifiers an
 * asset has populated. Used in lists, detail headers, and raw-inventory rows.
 *
 * Two ways to feed it:
 *   - <IdentifierBadges asset={asset} />               → inspects asset fields
 *   - <IdentifierBadges identifiers={['EPC','QR']} />  → explicit list (used
 *                                                        by raw-inventory rows
 *                                                        where the row already
 *                                                        carries the array)
 *
 * `highlight` (optional) marks one pill as the "matched" type for that scan
 * row — drawn slightly larger and bordered so the operator's eye lands on
 * "this scan came in as a QR" without comparing to the rest.
 */
const PALETTE = {
  EPC: { bg: '#e3f2fd', fg: '#1565c0', border: '#90caf9' },
  BAR: { bg: '#fff3e0', fg: '#ef6c00', border: '#ffcc80' },
  QR:  { bg: '#f3e5f5', fg: '#6a1b9a', border: '#ce93d8' },
  NFC: { bg: '#e8f5e9', fg: '#2e7d32', border: '#a5d6a7' },
  TID: { bg: '#eceff1', fg: '#455a64', border: '#cfd8dc' },
};

const fromAsset = (asset) => {
  if (!asset) return [];
  const out = [];
  if (asset.rfid_tag) out.push('EPC');
  if (asset.barcode)  out.push('BAR');
  if (asset.qr_code)  out.push('QR');
  if (asset.nfc_tag)  out.push('NFC');
  return out;
};

const Pill = ({ kind, highlight }) => {
  const c = PALETTE[kind] || PALETTE.EPC;
  return (
    <span
      title={
        kind === 'EPC' ? 'UHF RFID EPC tag registered' :
        kind === 'BAR' ? 'Barcode (1D) registered' :
        kind === 'QR'  ? 'QR code (2D) registered' :
        kind === 'NFC' ? 'NFC tag UID registered' :
        kind === 'TID' ? 'UHF TID identifier' : kind
      }
      style={{
        display: 'inline-block',
        padding: highlight ? '2px 8px' : '1px 6px',
        marginRight: 4,
        borderRadius: 4,
        fontSize: highlight ? 11 : 10,
        fontWeight: 700,
        letterSpacing: 0.4,
        background: c.bg,
        color: c.fg,
        border: highlight ? `1.5px solid ${c.border}` : '1px solid transparent',
        verticalAlign: 'middle',
      }}
    >
      {kind}
    </span>
  );
};

const IdentifierBadges = ({ asset, identifiers, highlight }) => {
  const list = identifiers || fromAsset(asset);
  if (!list || list.length === 0) {
    return <span style={{ color: 'var(--text-muted)', fontSize: 11 }}>—</span>;
  }
  return (
    <span style={{ whiteSpace: 'nowrap' }}>
      {list.map((kind) => (
        <Pill key={kind} kind={kind} highlight={highlight && highlight.toUpperCase() === kind} />
      ))}
    </span>
  );
};

export default IdentifierBadges;
